import React, { useState } from "react";

import { HStack, VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Image } from "@UI/Components/Image/Image";
import { Link } from "@UI/Components/Link/Link";
import { Icon } from "@UI/Components/Icon/Icon";
import { Divider } from "@UI/Components/Divider/Divider";
import { DetailLine } from "@UI/Components/DetailLine/DetailLine";
import { ModeratedContentInfo } from "../ModeratedContentInfo";
import {
  ModeratedInline,
  ModeratedText,
  hasModeratedContent,
  moderatedPlainText,
} from "../ModeratedText";
import { LocationDot } from "@UI/Components/Icons/LocationDot";
import { Calendar } from "@UI/Components/Icons/Calendar";
import { ChevronDown } from "@UI/Components/Icons/ChevronDown";
import { ImageCirclePlus } from "@UI/Components/Icons/ImageCirclePlus";
import { Button } from "@UI/Components/Button/Button";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { PhotoLightbox } from "@modules/ServiceRequest/ServiceRequestCreation/Components/Questions/PictureUploadQuestion/PhotoLightbox";
import { TASK_CREATION_FULL_FLAG } from "@modules/ServiceRequest/ServiceRequestCreation/config/constants";
import {
  IDraftAnswer,
  IPhotoAttachment,
} from "@modules/ServiceRequest/ServiceRequestCreation/types";
import {
  getQuestionTitleMessage,
  getQuestionOptionMessage,
  getConfigMessage,
} from "@modules/ServiceRequest/ServiceRequestCreation/utils/messageKeys";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@UI/Components/Accordion/Accordion";
import { Menu, MenuButton, MenuItem, MenuList } from "@UI/Components/Menu/Menu";
import { IconButton } from "@UI/Components/Button/IconButton";
import { Cog } from "@UI/Components/Icons/Cog";
import { useServiceRequestFlow } from "../../hooks/useServiceRequestFlow";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useServiceRequestCandidates } from "@modules/ServiceRequest/hooks/useCandidates";
import { ConfirmationModal } from "@Shared/Components/ConfirmationModal/ConfirmationModal";
import { EditServiceRequestModal } from "./EditServiceRequestModal";
import { AddPhotosModal } from "./AddPhotosModal/AddPhotosModal";
import { Tooltip } from "@UI/Components/Tooltip/Tooltip";
import useI18n from "@I18n/hooks/useI18n";
import { getLocale } from "@I18n/services/i18n";
import { dateFromNow } from "@utils/dateFromNow";
import { serviceRequestPathFragment } from "@modules/ServiceRequest/config/routes";

// Figma's thumbnail strip, duplicated from DescriptionField.tsx (RT-4321 M2)
// rather than shared — this card and the task-creation description step are
// meant to diverge, not stay in lockstep.
const PHOTO_THUMBNAIL_SIZE = { base: "32px", lg: "50px" };

const AVAILABILITY_QUESTION_KEY = "desired_timing";
const DESIRED_DATE_QUESTION_KEY = "desired_date";
const BEFORE_DATE_OPTION_VALUE = "before_date";
const ROOMS_QUESTION_KEY = "rooms";
const FLOOR_AREA_QUESTION_KEY = "floor_area_m2";
const AUDIENCE_QUESTION_KEY = "audience";
const EXTRA_DETAILS_QUESTION_KEY = "extra_details";

// Approved copy for the task-show page overrides the creation flow's own
// (longer, form-label) question titles. The "category" question's key varies
// by trade — task_type everywhere except appliance_repairer and painter.
const QUESTION_KEY_TITLE_MESSAGE: Record<string, keyof typeof messages> = {
  desired_timing: "newTaskShow.availability",
  task_type: "newTaskShow.category",
  appliance_type: "newTaskShow.category",
  surface_type: "newTaskShow.category",
  extra_details: "newTaskShow.details",
  audience: "newTaskShow.typeOfProvider",
};

export const ServiceRequestDetailCardAiAssisted = () => {
  const { serviceRequest } = useServiceRequestFlow();
  const { formatMessage } = useI18n();
  const { currentUser } = useCurrentUser({ includes: ["serviceProvider"] });
  const isServiceProvider = !!currentUser?.serviceProvider?.id;
  const { isOpen, onToggle } = useDisclosure({
    defaultIsOpen: isServiceProvider,
  });
  const {
    isOpen: isCancelConfirmationOpen,
    onOpen: onCancelConfirmationOpen,
    onClose: onCancelConfirmationClose,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isAddPhotosModalOpen,
    onOpen: onAddPhotosModalOpen,
    onClose: onAddPhotosModalClose,
  } = useDisclosure();
  const [photoAttachments, setPhotoAttachments] = useState<IPhotoAttachment[]>(
    serviceRequest?.photoAttachments ?? []
  );
  const navigate = useNavigate();
 const isTaskCreationEditEnabled = useFeatureFlag(TASK_CREATION_FULL_FLAG);


  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const categoryKey = serviceRequest?.sCategory;
  const answers = serviceRequest?.answers ?? [];

  const availabilityAnswer = answers.find(
    (answer) => answer.question_key === AVAILABILITY_QUESTION_KEY
  );
  const desiredDateAnswer = answers.find(
    (answer) => answer.question_key === DESIRED_DATE_QUESTION_KEY
  );
  const roomsAnswer = answers.find(
    (answer) => answer.question_key === ROOMS_QUESTION_KEY
  );
  const floorAreaAnswer = answers.find(
    (answer) => answer.question_key === FLOOR_AREA_QUESTION_KEY
  );
  // Every trade config's non-excluded questions (i.e. not availability, date,
  // rooms, floor area, address or a photo) boil down to exactly these three —
  // no generic catch-all is needed to avoid dropping data.
  const categoryAnswer = answers.find(
    (answer) =>
      QUESTION_KEY_TITLE_MESSAGE[answer.question_key] === "newTaskShow.category"
  );
  const audienceAnswer = answers.find(
    (answer) => answer.question_key === AUDIENCE_QUESTION_KEY
  );
  const detailsAnswer = answers.find(
    (answer) => answer.question_key === EXTRA_DETAILS_QUESTION_KEY
  );

  const answerTitle = (answer: IDraftAnswer): string => {
    const overrideKey = answer.question
      ? QUESTION_KEY_TITLE_MESSAGE[answer.question.key]
      : undefined;

    if (overrideKey) return formatMessage(messages[overrideKey]);

    const message = answer.question
      ? getQuestionTitleMessage(categoryKey, answer.question.key)
      : undefined;

    return message ? formatMessage(message) : answer.question_key;
  };

  const answerDetail = (answer: IDraftAnswer): React.ReactNode => {
    const { question, value } = answer;
    if (value == null || !question) return undefined;

    if (question.type === "single_select") {
      const message = getQuestionOptionMessage(
        categoryKey,
        question.key,
        String(value)
      );

      return message ? formatMessage(message) : String(value);
    }

    if (question.type === "multi_select") {
      return (Array.isArray(value) ? value : [])
        .map((option) => {
          const message = getQuestionOptionMessage(
            categoryKey,
            question.key,
            String(option)
          );

          return message ? formatMessage(message) : String(option);
        })
        .join(", ");
    }

    // Only a `text` answer goes through moderation, so only it can carry a
    // marker. Handled here rather than at each `DetailLine` so every open-text
    // answer is covered, whichever one the card renders.
    if (question.type === "text") return <ModeratedText text={String(value)} />;

    return String(value);
  };

  // "Before this date" needs its own answer (`desired_date`, a sibling
  // question triggered via `visible_if`) appended — the date itself isn't
  // part of the `desired_timing` answer's value.
  const availabilityDetail = (): React.ReactNode => {
    if (!availabilityAnswer) return undefined;

    const detail = answerDetail(availabilityAnswer);

    if (
      availabilityAnswer.value !== BEFORE_DATE_OPTION_VALUE ||
      !desiredDateAnswer?.value
    ) {
      return detail;
    }

    const formattedDate = new Date(
      String(desiredDateAnswer.value)
    ).toLocaleDateString(getLocale(), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    return `${detail} ${formattedDate}`;
  };

  // `rooms` and `floor_area_m2` (painter only) are each independently
  // conditional in the config — either can exist without the other — so this
  // joins whichever parts are actually present rather than assuming both.
  const surfaceDetailsValue = (): string | undefined => {
    const parts: string[] = [];

    if (roomsAnswer?.value != null) {
      const count = Number(roomsAnswer.value);

      parts.push(
        String(
          formatMessage(
            messages[
            count === 1
              ? "newTaskShow.surfaceDetails.room"
              : "newTaskShow.surfaceDetails.roomPlural"
            ],
            { count }
          )
        )
      );
    }

    if (floorAreaAnswer?.value != null) {
      // "20 m²" — a number and a unit that reads the same in every locale.
      parts.push(`${floorAreaAnswer.value} m²`);
    }

    return parts.length > 0 ? parts.join(" · ") : undefined;
  };

  // `hints_intro` is an i18n key the config carries verbatim, not literal
  // text — see `getConfigMessage`'s doc comment.
  const answerTooltip = (answer: IDraftAnswer): string | undefined => {
    const hintsIntro = answer.question?.hints_intro;
    if (!hintsIntro) return undefined;

    const message = getConfigMessage(hintsIntro);

    return message ? formatMessage(message) : undefined;
  };

  const displayPhotos = photoAttachments.map((photo) => ({
    id: String(photo.id),
    thumbUrl: photo.thumb_url,
    fullUrl: photo.normal_url,
    description: photo.description,
  }));

  // The four field groups `Tasks::HasModeration#moderatable_texts` covers.
  // Only a `text` answer is moderated, but scanning every answer costs nothing
  // and needs no update if the backend widens the set.
  const hasModeration = hasModeratedContent(
    serviceRequest?.title,
    serviceRequest?.description,
    ...answers.map((answer) =>
      typeof answer.value === "string" ? answer.value : undefined
    ),
    ...displayPhotos.map((photo) => photo.description)
  );

  const { candidates = [] } = useServiceRequestCandidates({
    serviceRequestId: serviceRequest?.id ?? "",
    sortBy: "recommended",
  });

  // The flow starts with no service request and fetches it, so every read
  // below this line would be a read off `undefined` on the first render. The
  // guard sits after the last hook, since a hook may not be skipped.
  if (!serviceRequest) return null;

  const serviceRequestHasOffers = candidates?.some(
    (candidates) => candidates?.serviceOffer
  );

  const handelServiceRequestCancellation = () => {
    navigate(
      serviceRequestHasOffers
        ? "LEGACY_HAS_CONTRACT_TASK_CANCEL"
        : "LEGACY_NO_CONTRACT_TASK_CANCEL",
      {
        reload: true,
        queryParams: { id: String(serviceRequest.id) },
      }
    );
  };

  return (
    <VStack
      w="full"
      alignItems="start"
      spacing="18px"
      background="white"
      p={{ base: "16px", lg: "24px" }}
      borderRadius="10px"
    >
      <HStack w="full" spacing="8px" justifyContent="space-between">
        <HStack
          spacing="6px"
          px="10px"
          py="4px"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="full"
        >
          {serviceRequest?.pCategoryId > 1 &&
            serviceRequest?.pCategoryId < 12 && (
              <Box w="16px" h="16px" flexShrink={0}>
                <Image
                  src={require(`assets/images/cat/svg/s_cat/${serviceRequest.pCategoryId}.svg`)}
                  w="16px"
                  h="16px"
                  alt=""
                />
              </Box>
            )}
          {messages[`s_cat.${serviceRequest?.sCategory}`] && (
            <Text fontSize="sm" color="gray.700" m="0">
              <FormattedMessage
                {...messages[`s_cat.${serviceRequest.sCategory}`]}
              />
            </Text>
          )}
        </HStack>
        <Menu>
          <MenuButton
            as={IconButton}
            variant="secondary"
            size="sm"
            alignSelf="start"
            aria-label={messages["actions"].defaultMessage}
            icon={<Icon as={Cog} width="14px" height="14px" />}
          />
          <MenuList minWidth="79px">
            {isTaskCreationEditEnabled && (
              <MenuItem onClick={onEditModalOpen}>
                <FormattedMessage {...messages["edit"]} />
              </MenuItem>
            )}
            <MenuItem
              onClick={
                serviceRequest?.appliedServiceProvidersCount < 1
                  ? onCancelConfirmationOpen
                  : handelServiceRequestCancellation
              }
            >
              <FormattedMessage
                {...messages["serviceRequest.declineOffer.cancelButton.text"]}
              />
            </MenuItem>
          </MenuList>
        </Menu>
        <ConfirmationModal
          title={formatMessage(messages["serviceRequest.cancellation.title"])}
          message={formatMessage(
            messages["serviceRequest.cancellation.message"]
          )}
          actionText={formatMessage(
            messages["serviceRequest.cancellation.confirmCTAText"]
          )}
          cancelActionText={formatMessage(
            messages["serviceRequest.cancellation.cancelCTAText"]
          )}
          isOpen={isCancelConfirmationOpen}
          onClose={onCancelConfirmationClose}
          onAction={handelServiceRequestCancellation}
        />
        <EditServiceRequestModal
          isOpen={isEditModalOpen}
          onClose={onEditModalClose}
          onContinue={() => {
            onEditModalClose();
            // A real load: the edit flow is its own Rails-served page
            // (`tasks_creation#edit`), the same handoff the legacy edit does.
            // No `withLocale`: the app's locale is a full code like `fr-BE`,
            // while the Rails route only accepts `fr` or `nl`, so prefixing it
            // 404s. The page reads the language from the session anyway.
            navigate(serviceRequestPathFragment.SERVICE_REQUEST_EDIT, {
              reload: true,
              params: { id: String(serviceRequest.id) },
            });
          }}
        />
        <AddPhotosModal
          isOpen={isAddPhotosModalOpen}
          onClose={onAddPhotosModalClose}
          serviceRequestId={serviceRequest.id}
          existingPhotos={photoAttachments}
          onConfirmed={setPhotoAttachments}
        />
      </HStack>
      <Tooltip
        label={moderatedPlainText(
          serviceRequest?.title,
          formatMessage(messages["moderatedContent.inlineLabel"])
        )}
      >
        <Heading
          fontSize={{ base: "2xl", md: "4xl" }}
          _firstLetter={{ textTransform: "uppercase" }}
          noOfLines={2}
        >
          <ModeratedInline text={serviceRequest?.title} />
        </Heading>
      </Tooltip>

      <DetailLine
        title={formatMessage(messages["newTaskShow.address"])}
        detail={serviceRequest?.placeName}
        icon={
          <Icon as={LocationDot} width="16px" height="16px" color="gray.500" />
        }
        underlined
      />
      <DetailLine
        title={
          availabilityAnswer
            ? answerTitle(availabilityAnswer)
            : formatMessage(messages["newTaskShow.availability"])
        }
        detail={availabilityDetail()}
        icon={
          <Icon as={Calendar} width="16px" height="16px" color="gray.500" />
        }
        underlined
      />

      <Accordion
        allowToggle
        border="none"
        w="full"
        defaultIndex={isServiceProvider ? [0] : []}
      >
        <AccordionItem border="none">
          <AccordionPanel p="0" pb={4}>
            <VStack w="full" alignItems="start" spacing="18px">
              <VStack w="full" alignItems="start" spacing="8px">
                <HStack
                  w="full"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Text
                    as="span"
                    m="0"
                    fontSize="xxs"
                    fontWeight="bold"
                    color="gray.900"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    {formatMessage(messages["newTaskShow.photos"])}
                  </Text>
                  {isTaskCreationEditEnabled && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      leftIcon={
                        <Icon as={ImageCirclePlus} width="4" height="4" />
                      }
                      onClick={onAddPhotosModalOpen}
                    >
                      <FormattedMessage
                        {...messages["newTaskShow.addPhotosButton"]}
                      />
                    </Button>
                  )}
                </HStack>
                {displayPhotos.length === 0 ? (
                  <Text
                    m="0"
                    fontSize="md"
                    fontStyle="italic"
                    color="gray.700"
                  >
                    <FormattedMessage {...messages["newTaskShow.noPhotos"]} />
                  </Text>
                ) : (
                  <Flex gap="8px" flexWrap="wrap">
                    {displayPhotos.map((photo, index) => (
                      <Box
                        key={photo.id}
                        flexShrink={0}
                        cursor="pointer"
                        onClick={() => setViewerIndex(index)}
                      >
                        <Image
                          src={photo.thumbUrl}
                          alt=""
                          boxSize={PHOTO_THUMBNAIL_SIZE}
                          objectFit="cover"
                          borderRadius="8px"
                        />
                      </Box>
                    ))}
                  </Flex>
                )}
                <PhotoLightbox
                  isOpen={viewerIndex != null}
                  index={viewerIndex ?? 0}
                  slides={displayPhotos.map((photo) => ({
                    src: photo.fullUrl,
                    description: photo.description,
                  }))}
                  onClose={() => setViewerIndex(null)}
                />
              </VStack>

              <Divider color="gray.300" />

              {categoryAnswer && (
                <DetailLine
                  title={answerTitle(categoryAnswer)}
                  detail={answerDetail(categoryAnswer)}
                  tooltip={answerTooltip(categoryAnswer)}
                />
              )}
              {(roomsAnswer || floorAreaAnswer) && (
                <DetailLine
                  title={formatMessage(messages["newTaskShow.surface"])}
                  detail={surfaceDetailsValue()}
                />
              )}
              <DetailLine
                title={formatMessage(messages["newTaskShow.description"])}
                detail={<ModeratedText text={serviceRequest?.description} />}
              />
              {detailsAnswer && (
                <DetailLine
                  title={answerTitle(detailsAnswer)}
                  detail={answerDetail(detailsAnswer)}
                />
              )}
              {hasModeration && <ModeratedContentInfo />}

              <Divider h="1px" color="gray.300" />

              {audienceAnswer && (
                <DetailLine
                  title={formatMessage(messages["newTaskShow.whoSeesRequest"])}
                  detail={answerDetail(audienceAnswer)}
                  tooltip={answerTooltip(audienceAnswer)}
                />
              )}
              {serviceRequest?.state === "posted" && (
                <DetailLine
                  title={formatMessage(messages["newTaskShow.status"])}
                  detail={`${formatMessage(
                    messages["serviceRequestStateType.posted"]
                  )} · ${dateFromNow(serviceRequest?.postedDate)}`}
                />
              )}

              <Divider color="gray.300" />

              <DetailLine
                title={formatMessage(messages["newTaskShow.requestedBy"])}
                detail={serviceRequest?.clientName}
              />
            </VStack>
          </AccordionPanel>
          <AccordionButton p="0" justifyContent="center">
            <Link
              h="fit-content"
              textDecoration="underline"
              color="pink.500"
              onClick={onToggle}
              _hover={{ color: "pink.600" }}
            >
              <Text m="0" mr="4px" display="inline">
                <FormattedMessage
                  {...messages[isOpen ? "hideDetails" : "seeDetails"]}
                />
              </Text>
              <Icon
                as={ChevronDown}
                width="16px"
                height="16px"
                transform={isOpen ? "rotate(180deg)" : undefined}
              />
            </Link>
          </AccordionButton>
        </AccordionItem>
      </Accordion>
    </VStack>
  );
};
